import React, { useState, useRef, useEffect } from "react";
import styles from "./Tooltip.module.scss";

type CustomizedTooltipProps = {
    tooltipContent: React.ReactNode | string;
    children: React.ReactNode;
};

const CustomizedTooltip: React.FC<CustomizedTooltipProps> = ({ tooltipContent, children }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [direction, setDirection] = useState<"top" | "bottom">("top");

    const updatePosition = () => {
        if (!wrapperRef.current || !tooltipRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const tooltip = tooltipRef.current;
        const tooltipRect = tooltip.getBoundingClientRect();

        const spacing = 6; // Distanza minore
        let top = rect.top - tooltipRect.height - spacing;
        let left = rect.left + rect.width / 2 - tooltipRect.width / 2;

        // Adattamento bordi
        const padding = 8;
        if (left < padding) left = padding;
        if (left + tooltipRect.width > window.innerWidth - padding) {
            left = window.innerWidth - tooltipRect.width - padding;
        }

        if (top < padding) {
            top = rect.bottom + spacing;
            setDirection("bottom");
        } else {
            setDirection("top");
        }

        setCoords({ top, left });
    };

    useEffect(() => {
        if (showTooltip) {
            updatePosition();
            window.addEventListener("scroll", updatePosition);
            window.addEventListener("resize", updatePosition);
        }
        return () => {
            window.removeEventListener("scroll", updatePosition);
            window.removeEventListener("resize", updatePosition);
        };
    }, [showTooltip]);

    const renderContent = () =>
        typeof tooltipContent === "string"
            ? <div dangerouslySetInnerHTML={{ __html: tooltipContent }} />
            : tooltipContent;

    return (
        <span
            ref={wrapperRef}
            className={styles.tooltipWrapper}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && (
                <div
                    ref={tooltipRef}
                    className={`${styles.customTooltip} ${styles[direction]}`}
                    style={{ top: coords.top, left: coords.left }}
                >
                    {renderContent()}
                    <div className={styles.tooltipArrow} />
                </div>
            )}
        </span>
    );
};

export default CustomizedTooltip;